import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ListChecks, ChevronDown, Search } from "lucide-react";
import AppContext from "../../context/AppContext";
import { getWorkflows } from "../../service/workflow";
import { getPermittedActivities } from "../../utils/activityPermissions";
import translations from "./Translations";
import CommonTooltip from "./CommonTooltip";

const MultipleActivitySelect = ({
  getWorkflowId,
  selectedOption,
  setSelectedOption,
  isIncludeDeletedRecords,
  setIsIncludeDeletedRecords,
  hideIncludeDeletedCheckbox = false,
  placeholder,
  layout = "stack",
}) => {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [panelPos, setPanelPos] = useState(null);
  const {
    setSelectedActivityNames,
    theme,
    setWorkflowId,
    langMode,
    userType,
    projectId,
  } = useContext(AppContext);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(getWorkflowId)) {
      setSelectedOption(getWorkflowId);
    }
  }, [getWorkflowId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getWorkflows(projectId, userType);
        const workflows = response.body.value;
        if (Array.isArray(workflows)) {
          const permitted = getPermittedActivities(workflows);
          setData(permitted);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error("Error fetching workflows:", error);
        setData([]);
      }
    };
    fetchData();
  }, [projectId, getWorkflowId, userType]);

  // Position the dropdown via a portal to document.body instead of relying on
  // in-flow absolute positioning. This component can be mounted inside the
  // fixed-height top header bar (see ExportData.js's headerActivitySelect),
  // whose own overflow/height clipped and distorted an in-flow panel.
  const placePanel = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPanelPos({
      top: rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, 240),
    });
  };

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    placePanel();
    const onDocMouseDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => placePanel();
    document.addEventListener("mousedown", onDocMouseDown);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const options = useMemo(
    () =>
      data.map((workflow) => ({
        value: workflow.workflowId,
        label: workflow.workflowName,
      })),
    [data]
  );

  const filteredOptions = useMemo(
    () =>
      options.filter((opt) =>
        String(opt.label || "").toLowerCase().includes(query.toLowerCase())
      ),
    [options, query]
  );

  const applySelection = (selectedList) => {
    const ids = selectedList.map((item) => item.value);
    const names = selectedList.map((item) => item.label);
    setSelectedOption(ids);
    setWorkflowId(ids);
    setSelectedActivityNames(names);
  };

  const toggleOption = (opt) => {
    const isSelected = selectedOption.includes(opt.value);
    const newIds = isSelected
      ? selectedOption.filter((id) => id !== opt.value)
      : [...selectedOption, opt.value];
    // Always derive from `options` (not newIds) so the stored order matches the
    // workflow list order — identical to the old react-select-derived selectedValues.
    applySelection(options.filter((o) => newIds.includes(o.value)));
  };

  const allSelected = options.length > 0 && selectedOption.length === options.length;
  const toggleSelectAll = () => applySelection(allSelected ? [] : options);

  const triggerLabel =
    selectedOption.length === 0
      ? placeholder || translations[langMode].select
      : `${translations[langMode].Activities} (${selectedOption.length})`;

  // Trigger colors/sizing mirror CommonReactSelector's default control style
  // exactly (same border/radius/height/shadow tokens the Operations/Project
  // header dropdowns use), so this looks like the same family of control —
  // only the leading icon + checkbox-list panel are custom.
  const themeClasses =
    theme === "dark"
      ? {
          trigger: "border-[#475569] bg-[#1e293b] text-gray-200",
          triggerHover: "hover:border-[#475569]",
          triggerOpen: "!border-[#818cf8] !shadow-[0_0_0_3px_rgba(129,140,248,0.25)]",
          panel: "border-gray-700 bg-gray-800",
          panelBorder: "border-gray-700",
          option: "text-gray-200 hover:bg-gray-700",
          optionActive: "bg-[#236DB4]/20 text-[#7fb3e0]",
          input: "bg-gray-900 text-gray-200 border-gray-600",
          muted: "text-gray-400",
        }
      : theme === "high-contrast"
        ? {
            trigger: "high-contrast border-2",
            triggerHover: "",
            triggerOpen: "",
            panel: "high-contrast border-2",
            panelBorder: "border-white",
            option: "high-contrast",
            optionActive: "high-contrast",
            input: "high-contrast border-2",
            muted: "",
          }
        : {
            trigger: "border-[#cbd5e1] bg-white text-gray-800",
            triggerHover: "hover:border-[#94a3b8]",
            triggerOpen: "!border-[#236DB4] !shadow-[0_0_0_3px_rgba(35,109,180,0.15)]",
            panel: "border-gray-200 bg-white",
            panelBorder: "border-gray-200",
            option: "text-gray-700 hover:bg-gray-50",
            optionActive: "bg-[#EAF2FA] text-[#236DB4]",
            input: "bg-white text-gray-800 border-gray-300",
            muted: "text-gray-400",
          };

  const panel = open && panelPos ? createPortal(
    <div
      ref={panelRef}
      style={{ position: "fixed", top: panelPos.top, left: panelPos.left, width: panelPos.width }}
      className={`z-[10060] overflow-hidden rounded-[10px] border shadow-lg ${themeClasses.panel}`}
    >
      <div className={`flex items-center gap-2 border-b px-3 py-1.5 ${themeClasses.panelBorder}`}>
        <Search size={14} className={`shrink-0 ${themeClasses.muted}`} />
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={translations[langMode].search}
          className={`h-7 w-full min-w-0 border-0 bg-transparent !p-0 text-sm outline-none focus:ring-0 ${
            theme === "dark" ? "text-gray-200 placeholder-gray-500" : theme === "high-contrast" ? "" : "text-gray-800 placeholder-gray-400"
          }`}
        />
      </div>

      <div className={`flex items-center justify-between border-b px-3 py-1.5 ${themeClasses.panelBorder}`}>
        <button
          type="button"
          onClick={toggleSelectAll}
          className="text-xs font-bold text-[#236DB4] hover:underline"
        >
          {allSelected ? translations[langMode].deselectAll : translations[langMode].selectAll}
        </button>
        <span className={`text-[11px] font-medium ${themeClasses.muted}`}>
          {selectedOption.length} {translations[langMode].Selected}
        </span>
      </div>

      <div className="max-h-[220px] overflow-y-auto p-1.5">
        {filteredOptions.length === 0 && (
          <div className={`px-3 py-3 text-center text-xs ${themeClasses.muted}`}>
            {translations[langMode]?.noAgentsMatchSearch ?? "No matches"}
          </div>
        )}
        {filteredOptions.map((opt) => {
          const isSelected = selectedOption.includes(opt.value);
          return (
            <label
              key={opt.value}
              className={`flex cursor-pointer items-start gap-2.5 rounded-[7px] px-2.5 py-2 text-sm transition-colors ${
                isSelected ? themeClasses.optionActive : themeClasses.option
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleOption(opt)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded accent-[#236DB4]"
              />
              <CommonTooltip text={opt.label} position="right" disableDefaultDescription className="min-w-0 flex-1">
                <span className="line-clamp-2 break-words">{opt.label}</span>
              </CommonTooltip>
            </label>
          );
        })}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div
      className={`relative flex min-w-[14rem] items-start gap-2 ${
        layout === "inline" ? "flex-nowrap" : "flex-wrap"
      } ${hideIncludeDeletedCheckbox ? "" : "justify-between"}`}
    >
      <div className={`relative ${layout === "inline" ? "min-w-0 flex-1 max-w-[340px]" : "w-full min-w-[14rem] max-w-[320px]"}`}>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={placeholder || translations[langMode].select}
          className={`flex h-[42px] w-full items-center gap-2 rounded-[10px] border px-3 text-[15px] font-medium shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-colors ${themeClasses.trigger} ${themeClasses.triggerHover} ${open ? themeClasses.triggerOpen : ""}`}
        >
          <ListChecks size={15} className="shrink-0 opacity-60" />
          <span className="flex-1 truncate text-left rtl:text-right">{triggerLabel}</span>
          <ChevronDown
            size={15}
            className={`shrink-0 opacity-60 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {panel}

      {!hideIncludeDeletedCheckbox && (
        <div className={`flex items-center gap-2 text-gray-700 dark:text-gray-300 ${layout === "inline" ? "pt-2 sm:pt-0" : ""}`}>
          <input
            type="checkbox"
            className={`rounded-sm h-4 w-4 ${isIncludeDeletedRecords ? "!bg-[#236DB4]" : ""}`}
            checked={isIncludeDeletedRecords}
            onChange={() =>
              setIsIncludeDeletedRecords?.(!isIncludeDeletedRecords)
            }
          />
          <label className="text-sm">
            {translations[langMode].IncludeDeletedRecords}
          </label>
        </div>
      )}
    </div>
  );
};
export default MultipleActivitySelect;
